import React, { useState, useEffect } from "react";
import { X, Maximize2, Minimize2 } from "lucide-react";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleIcon?: React.ReactNode;
  children: React.ReactNode;
  initialWidth?: string;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
  // Controlled fullscreen — when provided, expand/collapse is driven by the
  // parent (e.g. so entering an "edit" view can force fullscreen) instead of
  // the drawer's own internal state. Omit both for the original
  // internally-managed behavior; every existing caller keeps working as-is.
  fullscreen?: boolean;
  onFullscreenChange?: (next: boolean) => void;
}

const ReuseableDrawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  children,
  initialWidth = "45%",
  onFullscreenToggle,
  fullscreen,
  onFullscreenChange,
}) => {
  const isControlled = fullscreen !== undefined;
  const [width, setWidth] = useState(initialWidth);
  const [internalFullscreen, setInternalFullscreen] = useState(false);
  const isFullscreen = isControlled ? !!fullscreen : internalFullscreen;
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setWidth(initialWidth);
      if (!isControlled) setInternalFullscreen(false);
      if (onFullscreenToggle) onFullscreenToggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialWidth]);

  const toggleFullscreen = () => {
    const newState = !isFullscreen;
    if (isControlled) {
      onFullscreenChange?.(newState);
    } else {
      setInternalFullscreen(newState);
    }
    if (onFullscreenToggle) onFullscreenToggle(newState);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isFullscreen) return;
    setIsDragging(true);
    e.stopPropagation();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    const newWidth = `${Math.max(35, Math.min(100, (window.innerWidth - e.clientX) / window.innerWidth * 100))}%`;
    setWidth(newWidth);
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 h-full bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col transition-all duration-300 overflow-hidden`}
        style={{ width: isFullscreen ? "100%" : width }}
      >
        {!isFullscreen && (
          <div
            className="absolute left-0 top-0 h-full w-1 bg-gray-200 dark:bg-gray-700 hover:bg-blue-500 cursor-col-resize z-10"
            onMouseDown={handleMouseDown}
          />
        )}

        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-white dark:bg-gray-900 shrink-0">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
            {titleIcon}
            {title || "Campaign Report"}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleFullscreen}
              className="drawer-fullscreen-toggle p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </>
  );
};

export default ReuseableDrawer;